import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { MessageCircle, Send, CalendarPlus, X } from "lucide-react";
import BrandMark from "@/components/BrandMark";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export default function PublicAssistant() {
  const { slug } = useParams();
  const [info, setInfo] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", contact: "", date: "", time: "", detail: "", notes: "" });
  const [sent, setSent] = useState(false);
  const session = useRef("web-" + Math.random().toString(36).slice(2));
  const scroller = useRef(null);

  useEffect(() => {
    fetch(`${API}/public/assistant/${slug}`)
      .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
      .then((d) => { setInfo(d); setMsgs([{ role: "bot", text: d.welcome }]); })
      .catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => { if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight; }, [msgs]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setMsgs((m) => [...m, { role: "user", text }]);
    setInput(""); setSending(true);
    try {
      const res = await fetch(`${API}/public/assistant/${slug}/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: session.current }),
      });
      const d = await res.json();
      setMsgs((m) => [...m, { role: "bot", text: d.reply || "…" }]);
    } catch {
      setMsgs((m) => [...m, { role: "bot", text: "Ahora mismo no puedo responder. Inténtalo en un momento." }]);
    } finally { setSending(false); }
  };

  const submitBooking = async () => {
    if (!form.name.trim() || !form.contact.trim()) return;
    try {
      const res = await fetch(`${API}/public/assistant/${slug}/reservation`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setSent(true);
    } catch { /* noop */ }
  };

  if (notFound) return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg)] text-[var(--text-dim)]">
      Este asistente no está disponible.
    </div>
  );
  if (!info) return <div className="min-h-screen flex items-center justify-center bg-[var(--bg)] text-[var(--text-dim)]">Cargando…</div>;

  const f = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  return (
    <div className="min-h-screen bg-[var(--bg)] flex flex-col">
      {/* Cabecera del negocio */}
      <div className="border-b border-[var(--border)] bg-[var(--surface)]">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "var(--accent)", color: "var(--bg)" }}>
            <MessageCircle className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="font-display font-bold text-lg leading-none">{info.business_name}</div>
            <div className="label-mono mt-1">Asistente · disponible 24/7</div>
          </div>
          {info.booking_enabled && (
            <button onClick={() => { setShowForm(true); setSent(false); }}
                    className="flex items-center gap-2 px-3 py-2 text-sm font-semibold"
                    style={{ background: "var(--accent)", color: "var(--bg)" }} data-testid="pa-book">
              <CalendarPlus className="w-4 h-4" /> Reservar
            </button>
          )}
        </div>
      </div>

      {/* Chat */}
      <div ref={scroller} className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-4 py-6 space-y-3">
          {msgs.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className="max-w-[85%] px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap"
                   style={m.role === "user" ? { background: "var(--accent)", color: "var(--bg)" } : { background: "var(--surface-2)", color: "var(--text)" }}>
                {m.text}
              </div>
            </div>
          ))}
          {sending && <div className="label-mono text-[var(--text-dim)]">escribiendo…</div>}
        </div>
      </div>

      {/* Entrada */}
      <div className="border-t border-[var(--border)] bg-[var(--surface)]">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-2">
          <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                 placeholder="Escribe tu mensaje…"
                 className="flex-1 bg-[var(--bg)] border border-[var(--border)] px-3 py-2.5 text-sm text-white outline-none focus:border-[var(--accent)]" data-testid="pa-input" />
          <button onClick={send} disabled={sending} className="w-11 h-11 flex items-center justify-center disabled:opacity-50" style={{ background: "var(--accent)", color: "var(--bg)" }}>
            <Send className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-center justify-center gap-1.5 pb-2">
          <BrandMark className="w-3.5 h-3.5" />
          <span className="label-mono text-[10px] text-[var(--text-dim)]">con la tecnología de Agent Company OS</span>
        </div>
      </div>

      {/* Formulario de reserva */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-[var(--surface)] border border-[var(--border)] w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-display font-bold text-lg">Reservar en {info.business_name}</div>
              <button onClick={() => setShowForm(false)}><X className="w-5 h-5 text-[var(--text-dim)]" /></button>
            </div>
            {sent ? (
              <div className="text-center py-8">
                <div className="text-2xl mb-2">✅</div>
                <p className="text-sm text-white">¡Solicitud enviada!</p>
                <p className="text-xs text-[var(--text-dim)] mt-1">El negocio la confirmará en breve.</p>
                <button onClick={() => setShowForm(false)} className="mt-5 px-4 py-2 text-sm" style={{ background: "var(--accent)", color: "var(--bg)" }}>Cerrar</button>
              </div>
            ) : (
              <div className="space-y-3">
                <input value={form.name} onChange={(e) => f("name", e.target.value)} placeholder="Tu nombre *"
                       className="w-full bg-[var(--bg)] border border-[var(--border)] px-3 py-2 text-sm text-white outline-none focus:border-[var(--accent)]" data-testid="pa-name" />
                <input value={form.contact} onChange={(e) => f("contact", e.target.value)} placeholder="Teléfono o email *"
                       className="w-full bg-[var(--bg)] border border-[var(--border)] px-3 py-2 text-sm text-white outline-none focus:border-[var(--accent)]" data-testid="pa-contact" />
                <div className="grid grid-cols-2 gap-3">
                  <input type="date" value={form.date} onChange={(e) => f("date", e.target.value)}
                         className="bg-[var(--bg)] border border-[var(--border)] px-3 py-2 text-sm text-white outline-none focus:border-[var(--accent)]" />
                  <input type="time" value={form.time} onChange={(e) => f("time", e.target.value)}
                         className="bg-[var(--bg)] border border-[var(--border)] px-3 py-2 text-sm text-white outline-none focus:border-[var(--accent)]" />
                </div>
                <input value={form.detail} onChange={(e) => f("detail", e.target.value)} placeholder={info.booking_label}
                       className="w-full bg-[var(--bg)] border border-[var(--border)] px-3 py-2 text-sm text-white outline-none focus:border-[var(--accent)]" />
                <textarea value={form.notes} onChange={(e) => f("notes", e.target.value)} placeholder="Comentarios (opcional)" rows={2}
                          className="w-full bg-[var(--bg)] border border-[var(--border)] px-3 py-2 text-sm text-white outline-none focus:border-[var(--accent)]" />
                <button onClick={submitBooking} disabled={!form.name.trim() || !form.contact.trim()}
                        className="w-full py-2.5 text-sm font-semibold disabled:opacity-50" style={{ background: "var(--accent)", color: "var(--bg)" }} data-testid="pa-submit">
                  Enviar solicitud
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
